// 2P AI — Impostazioni (minimale in R1): dispositivi/passkey (lista + aggiungi riserva) e logout.
// La pagina completa (Dispositivi con revoca/rinomina, enrollment nuovo dispositivo, impostazioni
// generali) arriva in R5; qui si preserva la capacità di aggiungere una passkey di riserva.
import { useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { addPasskey, createEnrollCode, withStepUp } from '../lib/webauthn';
import { ago } from '../lib/time';
import { CommandBar } from '../components/CommandBar';
import { Crumb } from '../components/Crumb';
import { Icon } from '../components/Icon';

interface Device {
  id: string;
  label: string;
  transports: string[];
  first_seen: string | null;
  last_used: string | null;
}

export function Settings() {
  const [, navigate] = useLocation();
  const { logout } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get<{ items: Device[] }>('/auth/devices'),
    retry: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enroll, setEnroll] = useState<{ code: string; expires_in_min: number } | null>(null);
  const [copied, setCopied] = useState(false);

  async function addBackup() {
    setError(null);
    const label = window.prompt('Nome della passkey di riserva', 'Chiave di backup');
    if (!label) return;
    setBusy(true);
    try {
      await withStepUp('register_device', (token) => addPasskey(label, token));
      void qc.invalidateQueries({ queryKey: ['devices'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore');
    } finally {
      setBusy(false);
    }
  }

  async function enrollDevice() {
    setError(null);
    setEnroll(null);
    setCopied(false);
    const label = window.prompt('Nome del nuovo dispositivo (es. Mac di Paolo)', 'Mac');
    if (label === null) return;
    setBusy(true);
    try {
      const res = await withStepUp('register_device', (token) => createEnrollCode(label.trim(), token));
      setEnroll(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore');
    } finally {
      setBusy(false);
    }
  }

  async function copyCode() {
    if (!enroll) return;
    try {
      await navigator.clipboard.writeText(enroll.code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const devices = data?.items ?? [];

  return (
    <div className="app">
      <CommandBar />
      <div className="form">
        <Crumb current="Impostazioni" onBack={() => navigate('/')} />
        <div className="ftitle">Impostazioni</div>
        <div className="fsub">Dispositivi e sicurezza. La pagina completa arriva in R5.</div>

        <div className="fsection">
          <h3><Icon name="key" />Dispositivi · passkey</h3>
          {devices.length === 0 ? (
            <p className="help">Nessun dispositivo registrato.</p>
          ) : (
            devices.map((d) => (
              <div className="row-toggle" key={d.id}>
                <div>
                  <div className="tl">{d.label}</div>
                  <div className="td">
                    {d.transports.join(', ') || 'passkey'}
                    {d.last_used ? ` · ultimo uso ${ago(d.last_used)}` : ''}
                  </div>
                </div>
              </div>
            ))
          )}
          {error ? <p className="help err">{error}</p> : null}
          <div className="form-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button className="btn-ghost" type="button" onClick={() => void addBackup()} disabled={busy}>
              <Icon name="add" />{busy ? '…' : 'Aggiungi passkey su questo dispositivo'}
            </button>
            <button className="btn-ghost" type="button" onClick={() => void enrollDevice()} disabled={busy}>
              <Icon name="devices" />Aggiungi un altro dispositivo
            </button>
          </div>
        </div>

        {enroll ? (
          <div className="fsection">
            <h3><Icon name="qr_code_2" />Codice per il nuovo dispositivo</h3>
            <p className="help">
              Sul dispositivo nuovo apri <span className="mono">ai.2pweb.it</span>, tocca
              «Ho un codice» e inseriscilo. Vale una sola volta, scade tra {enroll.expires_in_min} minuti.
            </p>
            <div className="enroll-code" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0' }}>
              <code className="mono" style={{ fontSize: 18, letterSpacing: 1, userSelect: 'all' }}>{enroll.code}</code>
              <button className="btn-ghost" type="button" onClick={() => void copyCode()}>
                <Icon name={copied ? 'check' : 'content_copy'} />{copied ? 'Copiato' : 'Copia'}
              </button>
            </div>
          </div>
        ) : null}

        <div className="fsection">
          <h3><Icon name="logout" />Sessione</h3>
          <button className="btn-ghost danger" type="button" onClick={() => void logout()}>
            <Icon name="logout" />Esci
          </button>
        </div>
      </div>
    </div>
  );
}
